<template>
  <div
    v-if="variant === 'sidebar'"
    ref="sidebarRoot"
    class="sidebar-accueil-punch">
    <div class="row items-stretch no-wrap sidebar-accueil-punch__row">
      <slot name="left" />
      <div class="col">
        <button
          type="button"
          :class="['punch-card', `punch-card--${statusKind}`, 'punch-card--sidebar', { 'punch-card--open': isOpen }]"
          @click="onPillClick">
          <span :class="['punch-card__icon', `punch-card__icon--${statusKind}`]">
            <q-icon
              :name="statusIcon"
              size="18px" />
          </span>

          <span class="punch-card__body">
            <span :class="['punch-card__title', `punch-card__title--${statusKind}`]">{{ statusLabel }}</span>
            <span class="punch-card__meta">
              <q-icon
                :name="symRoundedSchedule"
                size="12px"
                class="punch-card__meta-icon" />
              <span class="punch-card__meta-text">{{ statusDetail || dateToDay(null, false) }}</span>
            </span>
          </span>

          <q-icon
            :name="symRoundedKeyboardArrowDown"
            size="20px"
            :class="['punch-card__caret', { 'punch-card__caret--open': isOpen }]" />
        </button>
      </div>
    </div>
    <div
      v-show="inlineOpen"
      class="sidebar-accueil-punch__panel">
      <PunchPanel
        inline
        v-bind="panelProps"
        @close="inlineOpen = false"
        @reload="reloadPunchStatus"
        @punch="onPunch"
        @request-confirm="onRequestConfirm" />
    </div>

    <Transition name="punch-confirm">
      <div
        v-if="pendingConfirm"
        :class="['sidebar-accueil-punch__confirm', { 'sidebar-accueil-punch__confirm--light': !$q.dark.isActive }]"
        @click.self="cancelConfirm">
        <div :class="['sidebar-accueil-punch__confirm-card', `sidebar-accueil-punch__confirm-card--${pendingConfirm.action.color}`]">
          <div class="sidebar-accueil-punch__confirm-title">
            <q-icon
              :name="pendingConfirm.action.icon"
              size="20px"
              class="sidebar-accueil-punch__confirm-icon" />
            {{ pendingConfirm.action.confirmLabel }}
          </div>
          <q-input
            v-if="!confirmInProgress"
            v-model="confirmComment"
            dense
            filled
            :placeholder="t('composites.PunchComponent.dialog.add_comment')"
            class="sidebar-accueil-punch__confirm-input" />
          <div
            v-if="confirmInProgress"
            class="sidebar-accueil-punch__confirm-spinner">
            <q-spinner
              :color="pendingConfirm.action.color"
              size="36px" />
          </div>
          <div
            v-else
            class="sidebar-accueil-punch__confirm-buttons">
            <q-btn
              flat
              no-caps
              label="Annuler"
              class="sidebar-accueil-punch__confirm-btn sidebar-accueil-punch__confirm-btn--cancel"
              @click="cancelConfirm" />
            <q-btn
              outline
              no-caps
              :color="pendingConfirm.action.color"
              label="Confirmer"
              class="sidebar-accueil-punch__confirm-btn"
              @click="confirmPending" />
          </div>
        </div>
      </div>
    </Transition>
  </div>

  <div
    v-else
    class="punch-card-wrapper punch-card-wrapper--header">
    <button
      type="button"
      :class="['punch-card', `punch-card--${statusKind}`, 'punch-card--header', { 'punch-card--open': isOpen }]">
      <span :class="['punch-card__icon', `punch-card__icon--${statusKind}`]">
        <q-icon
          :name="statusIcon"
          size="18px" />
      </span>

      <span class="punch-card__body">
        <span class="punch-card__title">{{ statusLabel }}</span>
        <span class="punch-card__meta">
          <q-icon
            :name="symRoundedSchedule"
            size="12px"
            class="punch-card__meta-icon" />
          <span class="punch-card__meta-text">{{ statusDetail || dateToDay(null, false) }}</span>
        </span>
      </span>

      <q-icon
        :name="symRoundedKeyboardArrowDown"
        size="20px"
        :class="['punch-card__caret', { 'punch-card__caret--open': isOpen }]" />

      <q-menu
        v-model="menuOpen"
        cover
        class="punch-menu">
        <PunchPanel
          v-bind="panelProps"
          @close="menuOpen = false"
          @reload="reloadPunchStatus"
          @punch="onPunch" />
      </q-menu>
    </button>
  </div>
</template>

<script setup lang="ts">
import {
  symRoundedHourglassEmpty,
  symRoundedKeyboardArrowDown,
  symRoundedLogin,
  symRoundedLogout,
  symRoundedPause,
  symRoundedPlayCircle,
  symRoundedSchedule,
  symRoundedWork,
} from '@quasar/extras/material-symbols-rounded'
import { onClickOutside } from '@vueuse/core'
import { useQuasar } from 'quasar'
import useDate from 'src/CrudManager/Core/composables/useDate'
import { usePunchStore } from 'src/stores/usePunchStore'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import PunchPanel from './components/PunchPanel.vue'
import { usePunchCalculations } from './composables/usePunchCalculations'
import { usePunchTimers } from './composables/usePunchTimers'

type PunchType = 'IN' | 'OUT' | 'BREAK_START' | 'BREAK_END'
type StatusKind = 'idle' | 'working' | 'break' | 'done'

const props = withDefaults(
  defineProps<{
    variant?: 'header' | 'sidebar'
  }>(),
  {
    variant: 'header',
  },
)

const { t } = useI18n()
const $q = useQuasar()
const { formatTime, dateToDay } = useDate()
const punchStore = usePunchStore()
const punchCalculations = usePunchCalculations()
const punchTimers = usePunchTimers()

const loading = ref(false)
const menuOpen = ref(false)
const inlineOpen = ref(false)
const sidebarRoot = ref<HTMLElement | null>(null)
const pendingConfirm = ref<{ action: PunchAction } | null>(null)
const confirmInProgress = ref(false)
const confirmComment = ref('')
const lastUpdateTime = ref(new Date().toISOString())

const isOpen = computed(() => (props.variant === 'sidebar' ? inlineOpen.value : menuOpen.value))

const updateLastUpdateTime = () => {
  lastUpdateTime.value = new Date().toISOString()
}

const statusKind = computed<StatusKind>(() => {
  if (punchStore.isOnBreak) return 'break'
  if (punchStore.hasPunchedOutToday) return 'done'
  if (punchStore.hasPunchedInToday) return 'working'
  return 'idle'
})

const statusPillColor = computed(() => {
  switch (statusKind.value) {
    case 'working':
      return 'positive'
    case 'break':
      return 'warning'
    case 'done':
      return 'muted'
    default:
      return 'default'
  }
})

const statusIcon = computed(() => {
  switch (statusKind.value) {
    case 'working':
      return symRoundedWork
    case 'break':
      return symRoundedPause
    case 'done':
      return symRoundedLogout
    default:
      return symRoundedHourglassEmpty
  }
})

const statusLabel = computed(() => {
  switch (statusKind.value) {
    case 'working':
      return t('composites.PunchComponent.working')
    case 'break':
      return t('composites.PunchComponent.on_break')
    case 'done':
      return t('composites.PunchComponent.day_done')
    default:
      return t('composites.PunchComponent.not_punched')
  }
})

const statusSince = computed(() => {
  switch (statusKind.value) {
    case 'working':
      return t('composites.PunchComponent.since', { time: formatTime(punchCalculations.punchInTime.value, false) })
    case 'break':
      return punchTimers.breakDuration.value ? t('composites.PunchComponent.break_since', { time: punchTimers.breakDuration.value }) : ''
    case 'done':
      return t('composites.PunchComponent.done_at', { time: formatTime(punchCalculations.punchOutTime.value, false) })
    default:
      return ''
  }
})

const statusElapsed = computed(() => {
  if (statusKind.value === 'working') {
    return punchCalculations.rawWorkTime.value || ''
  }
  return ''
})

const statusDetail = computed(() => {
  return [statusSince.value, statusElapsed.value].filter(Boolean).join(' · ')
})

interface PunchAction {
  type: PunchType
  color: string
  icon: string
  label: string
  confirmLabel: string
}

const availableActions = computed<PunchAction[]>(() => {
  const actions: PunchAction[] = []
  const confirm = (type: PunchType) => t(`composites.PunchComponent.confirm_prompts.${type}`)
  switch (statusKind.value) {
    case 'idle':
      actions.push({ type: 'IN', color: 'positive', icon: symRoundedLogin, label: t('composites.PunchComponent.punch_button_in'), confirmLabel: confirm('IN') })
      break
    case 'working':
      actions.push({
        type: 'BREAK_START',
        color: 'warning',
        icon: symRoundedPause,
        label: t('composites.PunchComponent.punch_button_break_start') || 'Prendre une pause',
        confirmLabel: confirm('BREAK_START'),
      })
      actions.push({
        type: 'OUT',
        color: 'negative',
        icon: symRoundedLogout,
        label: t('composites.PunchComponent.punch_button_out'),
        confirmLabel: confirm('OUT'),
      })
      break
    case 'break':
      actions.push({
        type: 'BREAK_END',
        color: 'info',
        icon: symRoundedPlayCircle,
        label: t('composites.PunchComponent.punch_button_break_end'),
        confirmLabel: confirm('BREAK_END'),
      })
      break
  }
  return actions
})

const panelProps = computed(() => ({
  light: !$q.dark.isActive,
  statusIcon: statusIcon.value,
  statusLabel: statusLabel.value,
  statusPillColor: statusPillColor.value,
  statusSince: statusSince.value,
  statusElapsed: statusElapsed.value,
  availableActions: availableActions.value,
  rawWorkTime: punchCalculations.rawWorkTime.value || '',
  billableTime: punchCalculations.billableWorkTime.value || '',
  totalBreak: punchCalculations.totalBreakTime.value || '',
  plannedTravelTime: punchCalculations.plannedTravelTime.value || '',
  breaks: punchCalculations.breaks.value,
  loading: loading.value,
  dateLabel: dateToDay(null, false),
  lastUpdateText: formatTime(lastUpdateTime.value),
}))

const onPillClick = () => {
  if (props.variant === 'sidebar') {
    inlineOpen.value = !inlineOpen.value
  }
}

onClickOutside(sidebarRoot, () => {
  if (props.variant !== 'sidebar') return
  if (confirmInProgress.value) return
  inlineOpen.value = false
  pendingConfirm.value = null
  confirmComment.value = ''
})

const onRequestConfirm = (action: PunchAction) => {
  confirmComment.value = ''
  pendingConfirm.value = { action }
}

const cancelConfirm = () => {
  if (confirmInProgress.value) return
  pendingConfirm.value = null
  confirmComment.value = ''
}

const confirmPending = async () => {
  if (!pendingConfirm.value || confirmInProgress.value) return
  const { action } = pendingConfirm.value
  confirmInProgress.value = true
  try {
    await runPunch(action.type, confirmComment.value)
  } finally {
    confirmInProgress.value = false
    pendingConfirm.value = null
    confirmComment.value = ''
  }
}

const onPunch = (type: PunchType, comment: string) => {
  void runPunch(type, comment)
}

const runPunch = async (type: PunchType, comment: string) => {
  loading.value = true
  try {
    await punchStore.punch(type, comment)
    await punchStore.syncPunchStatuses()
    updateLastUpdateTime()
  } catch (error) {
    console.error('Error during punch operation:', error)
  } finally {
    loading.value = false
  }
}

const reloadPunchStatus = async () => {
  loading.value = true
  try {
    await punchStore.syncPunchStatuses()
    updateLastUpdateTime()
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  loading.value = true
  try {
    await punchStore.syncPunchStatuses()
    updateLastUpdateTime()
    punchTimers.setupTimers(punchCalculations.breakStartTime, punchCalculations.punchInTime, punchCalculations.punchOutTime, updateLastUpdateTime)
  } catch (error) {
    console.error('Error syncing punch statuses:', error)
  } finally {
    loading.value = false
  }
})
</script>

<style lang="scss" scoped>
.sidebar-accueil-punch {
  position: relative;
}

.sidebar-accueil-punch__confirm {
  position: absolute;
  inset: 0;
  background: rgba(10, 14, 20, 0.78);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border-radius: 10px;
  z-index: 20;

  &--light {
    background: rgba(247, 248, 250, 0.85);
  }
}

.sidebar-accueil-punch__confirm-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 12px 10px;
  border-radius: 10px;
  background: #1c2129;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  width: 100%;
  max-width: 240px;
  color: #e7eaee;

  @mixin tone($color) {
    border-color: color-mix(in srgb, $color 50%, transparent);

    .sidebar-accueil-punch__confirm-icon {
      color: color-mix(in srgb, $color 60%, #fff);
    }
  }

  &--positive {
    @include tone(var(--q-positive));
  }
  &--negative {
    @include tone(var(--q-negative));
  }
  &--warning {
    @include tone(var(--q-warning));
  }
  &--info {
    @include tone(var(--q-info));
  }
}

.body--light .sidebar-accueil-punch__confirm-card {
  background: #fff;
  border-color: rgba(0, 0, 0, 0.1);
  color: #1a1f29;
}

.sidebar-accueil-punch__confirm-icon {
  opacity: 0.9;
}

.sidebar-accueil-punch__confirm-title {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.25;
}

.sidebar-accueil-punch__confirm-buttons {
  display: flex;
  gap: 6px;
  width: 100%;
  margin-top: 4px;
}

.sidebar-accueil-punch__confirm-spinner {
  display: flex;
  justify-content: center;
  padding: 4px 0 2px;
}

.sidebar-accueil-punch__confirm-input {
  width: 100%;
}

.sidebar-accueil-punch__confirm-btn {
  flex: 1 1 0;
  min-width: 0;

  &--cancel {
    opacity: 0.7;
  }
}

.punch-confirm-enter-active,
.punch-confirm-leave-active {
  transition: opacity 0.15s ease;
}

.punch-confirm-enter-from,
.punch-confirm-leave-to {
  opacity: 0;
}

.punch-card-wrapper {
  display: inline-block;

  &--sidebar {
    display: block;
    width: 100%;
  }
}

.punch-card {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px 6px 8px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid transparent;
  color: #fff;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;
  font: inherit;
  text-align: left;
  min-width: 0;
  max-width: 320px;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: transparent;
  }

  &--sidebar {
    background: rgba(255, 255, 255, 0.05);
    border-color: transparent;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    box-shadow: none;
    max-width: none;
    width: 100%;
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-template-rows: auto auto;
    grid-template-areas:
      'icon title caret'
      'metaicon meta caret';
    column-gap: 8px;
    row-gap: 1px;
    align-items: center;

    &:hover {
      background: rgba(255, 255, 255, 0.08);
      border-color: transparent;
    }
  }

  &--sidebar &__icon {
    grid-area: icon;
    background: transparent;
    box-shadow: none;
    width: 22px;
    height: 22px;
    border-radius: 0;
    color: inherit;
    justify-self: center;

    :deep(.q-icon) {
      font-size: 22px !important;
    }

    &--working {
      background: transparent;
      color: var(--q-positive);
    }

    &--break {
      background: transparent;
      color: var(--q-warning);
    }

    &--done {
      background: transparent;
      color: inherit;
    }
  }

  &--sidebar &__body {
    display: contents;
  }

  &--sidebar &__title {
    grid-area: title;
  }

  &--sidebar &__meta {
    display: contents;
  }

  &--sidebar &__meta-icon {
    grid-area: metaicon;
    justify-self: center;
    font-size: 16px !important;
  }

  &--sidebar &__meta-text {
    grid-area: meta;
  }

  &--sidebar &__caret {
    grid-area: caret;
    align-self: center;
  }

  .body--light &--sidebar {
    background: rgba(0, 0, 0, 0.04);
    color: #1a1f29;

    &:hover {
      background: rgba(0, 0, 0, 0.06);
    }

    .punch-card__title {
      color: #1a1f29;
    }

    .punch-card__meta {
      color: #6b7280;
    }

    .punch-card__caret {
      color: rgba(0, 0, 0, 0.55);
    }

    &:hover .punch-card__caret {
      color: #1a1f29;
    }
  }

  &--done {
    opacity: 0.85;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 9px;
    color: #fff;
    flex-shrink: 0;
    background: rgba(255, 255, 255, 0.22);
    box-shadow: 0 1px 0 rgba(255, 255, 255, 0.12) inset;

    &--working {
      background: color-mix(in srgb, var(--q-positive) 75%, transparent);
    }

    &--break {
      background: color-mix(in srgb, var(--q-warning) 78%, transparent);
      color: #2b1d00;
    }

    &--done {
      background: rgba(255, 255, 255, 0.18);
      color: rgba(255, 255, 255, 0.9);
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
    line-height: 1.15;
  }

  &__title {
    font-size: 0.95rem;
    font-weight: 700;
    letter-spacing: 0.005em;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &--working {
      color: var(--q-positive);
    }
    &--break {
      color: var(--q-warning);
    }
    &--done {
      color: rgba(255, 255, 255, 0.7);
    }
    &--idle {
      color: rgba(255, 255, 255, 0.85);
    }
  }

  .body--light &__title {
    &--working {
      color: color-mix(in srgb, var(--q-positive) 70%, #000);
    }
    &--break {
      color: color-mix(in srgb, var(--q-warning) 70%, #000);
    }
    &--done {
      color: rgba(0, 0, 0, 0.55);
    }
    &--idle {
      color: rgba(0, 0, 0, 0.7);
    }
  }

  &__meta {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.7rem;
    line-height: 1.2;
    color: rgba(255, 255, 255, 0.78);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__meta-icon {
    opacity: 0.85;
    flex-shrink: 0;
  }

  &__meta-text {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__caret {
    color: rgba(255, 255, 255, 0.7);
    flex-shrink: 0;
    transition: transform 0.18s ease;

    &--open {
      transform: rotate(180deg);
    }
  }

  &:hover &__caret {
    color: #fff;
  }
}
</style>

<style lang="scss">
.punch-menu {
  background: transparent !important;
  box-shadow: none !important;
  overflow: visible !important;
}
</style>
